import {
  Button,
  Card,
  Descriptions,
  Divider,
  Popconfirm,
  Skeleton,
  Space,
  Table,
  Tag,
  Typography,
  type TableProps,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DollarOutlined,
  TruckOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '@mall/shared';
import {
  cancelOrder,
  completeOrder,
  getOrder,
  payOrder,
  shipOrder,
} from './api';
import {
  ORDER_STATUS_COLOR,
  ORDER_STATUS_LABEL,
  type OrderItemView,
  type OrderStatus,
} from './types';
import { useToast } from '../../app/ToastProvider';

const PRICE = (cents: number) => `¥ ${(cents / 100).toFixed(2)}`;
const DT = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('zh-CN', { hour12: false }) : '—';

interface ActionDef {
  key: string;
  label: string;
  icon: React.ReactNode;
  danger?: boolean;
  type?: 'primary';
  enabledFor: OrderStatus[];
  run: (id: number) => Promise<void>;
  successMessage: string;
}

const ACTIONS: ActionDef[] = [
  {
    key: 'pay',
    label: '标记已支付',
    icon: <DollarOutlined />,
    type: 'primary',
    enabledFor: ['PENDING_PAYMENT'],
    run: payOrder,
    successMessage: '订单已标记为已支付',
  },
  {
    key: 'ship',
    label: '发货',
    icon: <TruckOutlined />,
    enabledFor: ['PAID'],
    run: shipOrder,
    successMessage: '已发货',
  },
  {
    key: 'complete',
    label: '完成',
    icon: <CheckCircleOutlined />,
    enabledFor: ['SHIPPED'],
    run: completeOrder,
    successMessage: '订单已完成',
  },
  {
    key: 'cancel',
    label: '取消',
    icon: <CloseCircleOutlined />,
    danger: true,
    enabledFor: ['PENDING_PAYMENT', 'PAID'],
    run: cancelOrder,
    successMessage: '订单已取消',
  },
];

export function OrderDetailPage() {
  const params = useParams<{ id: string }>();
  const id = Number(params.id);
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['admin', 'orders', id],
    queryFn: () => getOrder(id),
    enabled: !Number.isNaN(id),
  });

  const mutate = useMutation({
    mutationFn: async (action: ActionDef) => {
      await action.run(id);
      return action;
    },
    onSuccess: (action) => {
      toast.success(action.successMessage);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'orders', id] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'orders'] });
    },
    onError: (err: Error) => {
      toast.error(err instanceof ApiError ? err.message : '操作失败');
    },
  });

  if (query.isLoading) return <Skeleton active />;
  if (!query.data) {
    return (
      <Typography.Paragraph type="danger">
        订单不存在或加载失败
      </Typography.Paragraph>
    );
  }

  const order = query.data;
  const itemColumns: TableProps<OrderItemView>['columns'] = [
    { title: '商品编码', dataIndex: 'productCode', width: 160, render: (c: string) => <code>{c}</code> },
    { title: '商品名称', dataIndex: 'productName' },
    { title: '数量', dataIndex: 'quantity', width: 80, align: 'right' },
    { title: '单价', dataIndex: 'unitPriceCents', width: 120, align: 'right', render: PRICE },
    { title: '小计', dataIndex: 'totalCents', width: 120, align: 'right', render: PRICE },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/orders')}>
          返回列表
        </Button>
        <Typography.Title level={4} style={{ margin: 0 }}>
          订单 {order.orderNo}
        </Typography.Title>
        <Tag color={ORDER_STATUS_COLOR[order.status]}>{ORDER_STATUS_LABEL[order.status]}</Tag>
      </Space>

      <Card>
        <Descriptions column={{ xs: 1, sm: 2, md: 3 }} bordered size="small">
          <Descriptions.Item label="订单号">{order.orderNo}</Descriptions.Item>
          <Descriptions.Item label="客户姓名">{order.customerName}</Descriptions.Item>
          <Descriptions.Item label="电话">{order.customerPhone || '—'}</Descriptions.Item>
          <Descriptions.Item label="地址" span={3}>
            {order.customerAddress}
          </Descriptions.Item>
          <Descriptions.Item label="总额">{PRICE(order.totalCents)}</Descriptions.Item>
          <Descriptions.Item label="备注" span={2}>
            {order.remark || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="创建时间">{DT(order.createdAt)}</Descriptions.Item>
          <Descriptions.Item label="支付时间">{DT(order.paidAt)}</Descriptions.Item>
          <Descriptions.Item label="发货时间">{DT(order.shippedAt)}</Descriptions.Item>
          <Descriptions.Item label="完成时间">{DT(order.completedAt)}</Descriptions.Item>
          <Descriptions.Item label="取消时间">{DT(order.cancelledAt)}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Divider orientation="left">行项目</Divider>
      <Table<OrderItemView>
        rowKey="id"
        columns={itemColumns}
        dataSource={order.items}
        pagination={false}
        size="small"
        scroll={{ x: 'max-content' }}
        summary={(rows) => {
          const total = rows.reduce((acc, r) => acc + r.totalCents, 0);
          return (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={4} align="right">
                合计
              </Table.Summary.Cell>
              <Table.Summary.Cell index={4} align="right">
                <b>{PRICE(total)}</b>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          );
        }}
      />

      <Divider orientation="left">操作</Divider>
      <Space wrap>
        {ACTIONS.map((action) => {
          const enabled = action.enabledFor.includes(order.status);
          const button = (
            <Button
              key={action.key}
              icon={action.icon}
              type={action.type}
              danger={action.danger}
              disabled={!enabled || mutate.isPending}
            >
              {action.label}
            </Button>
          );
          if (!enabled) return button;
          return (
            <Popconfirm
              key={action.key}
              title={`确认「${action.label}」订单 ${order.orderNo}？`}
              onConfirm={() => mutate.mutate(action)}
            >
              {button}
            </Popconfirm>
          );
        })}
      </Space>
    </div>
  );
}
